// src/oura/ask/Mascot.tsx
//
// Ask Oura (PREVIEW): Oura, the mascot (v8 pack, 23 Oct 2026). Everywhere
// Oura acts as the assistant it shows its still face (OuraFace). It moves
// only in six moments, each one saying what Oura is doing:
//
//   Idle       greets a new chat (springs in on the calm smile, the one calm loop)
//   Thinking   while Oura reads the client's records
//   Evolving   while Oura drafts work
//   Happy      once, after a named yes
//   Love       once, when the chat opens on answers the client approved
//   Surprised  once, when an answer finds something late or come back
//
// One slot: at most one Oura moves on screen. A moment that cannot have the
// slot shows its still until it can; each one-off plays once, then settles
// to a still with a crossfade. Hovering Oura's face in the chat header or
// the greeting gives a wink; typing makes the greeting look up, attentive.
// prefers-reduced-motion shows the right mood's still and nothing moves.
// Decorative: the text beside Oura already says what is happening.
//
// Files: real alpha, 2x display size, built from the v8 pack by
// _work/ask-oura-3cases/mascot/make_mascot.py (matte.py does the matting).

import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from "react";
import { cn } from "../lib/cn";
import idle from "./mascot/idle.webp";
import idleWink from "./mascot/idle-wink.webp";
import idleStill from "./mascot/idle-still.webp";
import attentiveStill from "./mascot/attentive-still.webp";
import thinking from "./mascot/thinking.webp";
import thinkingStill from "./mascot/thinking-still.webp";
import evolving from "./mascot/evolving.webp";
import evolvingStill from "./mascot/evolving-still.webp";
import happy from "./mascot/happy.webp";
import happyStill from "./mascot/happy-still.webp";
import love from "./mascot/love.webp";
import loveStill from "./mascot/love-still.webp";
import surprised from "./mascot/surprised.webp";
import surprisedStill from "./mascot/surprised-still.webp";
import face16 from "./mascot/face-16.webp";
import face17 from "./mascot/face-17.webp";
import face20 from "./mascot/face-20.webp";
import face24 from "./mascot/face-24.webp";
import faceWink from "./mascot/face-wink-20.webp";

export type MascotMood = "idle" | "thinking" | "evolving" | "happy" | "love" | "surprised";

/** Each mood's moving file, its still, and how long one play lasts (from mascot.json). */
const MOOD: Record<MascotMood, { moving: string; still: string; ms: number }> = {
    idle: { moving: idle, still: idleStill, ms: 2976 },
    thinking: { moving: thinking, still: thinkingStill, ms: 2976 },
    evolving: { moving: evolving, still: evolvingStill, ms: 2976 },
    happy: { moving: happy, still: happyStill, ms: 2976 },
    love: { moving: love, still: loveStill, ms: 2666 },
    surprised: { moving: surprised, still: surprisedStill, ms: 1984 },
};
const WINK_MS = 1674;

/** The shared canvas (every mood but Happy): where the resting helmet sits, as a fraction of the canvas side. */
const REST = { w: 0.798, cx: 0.485, cy: 0.433 };
/** How much of Oura's still face the helmet fills. */
const FACE_HELMET = 0.905;

/** Who may move now: the moment with the highest priority, the newest one on a tie. */
const MASCOT_PRIORITY = { greeting: 0, wink: 1, moment: 2, working: 3 } as const;

const claims = new Map<number, { priority: number; at: number }>();
const listeners = new Set<() => void>();
let owner: number | null = null;
let nextId = 1;
let tick = 0;
function recompute() {
    let best: number | null = null;
    let bp = -1;
    let bat = -1;
    claims.forEach((c, id) => {
        if (c.priority > bp || (c.priority === bp && c.at > bat)) {
            best = id;
            bp = c.priority;
            bat = c.at;
        }
    });
    if (best !== owner) {
        owner = best;
        listeners.forEach((l) => l());
    }
}
const subscribe = (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
};

/** True while this component holds the one moving slot. */
function useSlot(wants: boolean, priority: number): boolean {
    const id = useRef(0);
    if (!id.current) id.current = nextId++;
    useEffect(() => {
        const me = id.current;
        if (wants) claims.set(me, { priority, at: ++tick });
        else claims.delete(me);
        recompute();
        return () => {
            claims.delete(me);
            recompute();
        };
    }, [wants, priority]);
    return useSyncExternalStore(subscribe, () => owner === id.current, () => false);
}

const REDUCE = "(prefers-reduced-motion: reduce)";
function useReducedMotion(): boolean {
    const [reduce, setReduce] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(REDUCE).matches);
    useEffect(() => {
        const mq = window.matchMedia?.(REDUCE);
        if (!mq) return;
        const on = () => setReduce(mq.matches);
        mq.addEventListener("change", on);
        return () => mq.removeEventListener("change", on);
    }, []);
    return reduce;
}

/**
 * A one-off must start at its first frame. Browsers share one animation clock per image URL, so each play
 * gets its own object URL of the same bytes (fetched once). `null` until it is ready: show the still meanwhile.
 */
const bytes = new Map<string, Promise<Blob>>();
function useFreshSrc(src: string | null, play: number): string | null {
    const [url, setUrl] = useState<string | null>(null);
    useEffect(() => {
        setUrl(null);
        if (!src) return;
        let alive = true;
        let made: string | null = null;
        let p = bytes.get(src);
        if (!p) {
            p = fetch(src).then((r) => r.blob());
            bytes.set(src, p);
        }
        p.then((b) => {
            if (!alive) return;
            made = URL.createObjectURL(b);
            setUrl(made);
        }).catch(() => alive && setUrl(src));
        return () => {
            alive = false;
            if (made) URL.revokeObjectURL(made);
        };
    }, [src, play]);
    return url;
}

const KEYFRAMES = `
@keyframes askMascotIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes askMascotOut { from { opacity: 1; } to { opacity: 0; } }
@keyframes askMascotSpring { 0% { opacity: 0; transform: translateY(6px) scale(0.72); } 55% { opacity: 1; transform: translateY(-1px) scale(1.05); } 78% { transform: translateY(0) scale(0.985); } 100% { opacity: 1; transform: none; } }
@keyframes askMascotSettle { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.74); } }
@keyframes askMascotPop { 0% { opacity: 0; transform: scale(0.66); } 60% { opacity: 1; transform: scale(1.04); } 100% { opacity: 1; transform: scale(1); } }
`;
let styled = false;
function useKeyframes() {
    useEffect(() => {
        if (styled || typeof document === "undefined") return;
        styled = true;
        const s = document.createElement("style");
        s.setAttribute("data-ask-mascot-style", "");
        s.textContent = KEYFRAMES;
        document.head.appendChild(s);
    }, []);
}

/** Two stacked images: a new picture fades in over the old one, which fades out. No layout shift, no hard cut. */
function Crossfade({ src, size, fade = 220 }: { src: string; size: number; fade?: number }) {
    const [layers, setLayers] = useState([{ src, id: 0 }]);
    const n = useRef(0);
    useEffect(() => {
        setLayers((ls) => (ls[ls.length - 1].src === src ? ls : [...ls.slice(-1), { src, id: ++n.current }]));
        const t = window.setTimeout(() => setLayers((ls) => ls.slice(-1)), fade + 40);
        return () => window.clearTimeout(t);
    }, [src, fade]);
    return (
        <>
            {layers.map((l, i) => (
                <img
                    key={l.id}
                    src={l.src}
                    alt=""
                    aria-hidden
                    draggable={false}
                    width={size}
                    height={size}
                    className="pointer-events-none absolute inset-0 select-none"
                    style={{
                        width: size,
                        height: size,
                        animation: layers.length > 1 ? `${i === layers.length - 1 ? "askMascotIn" : "askMascotOut"} ${fade}ms ease-out both` : undefined,
                    }}
                />
            ))}
        </>
    );
}

/**
 * Oura in one mood at `size` css px. `loop`: moves while it holds the slot (greeting, reading, drafting).
 * `once`: plays one time when it gets the slot, then settles to its still with a crossfade.
 */
export function Mascot({
    mood,
    size,
    mode = "loop",
    playKey,
    className,
    style,
}: {
    mood: MascotMood;
    size: number;
    mode?: "loop" | "once";
    /** A new value plays a `once` moment again, even in the same mood. */
    playKey?: string | number;
    className?: string;
    style?: CSSProperties;
}) {
    useKeyframes();
    const reduce = useReducedMotion();
    const [done, setDone] = useState(false);
    // A new mood, mode or play is a new moment: it plays, whatever the last one did.
    const playId = `${mood}|${mode}|${playKey ?? ""}`;
    const [lastPlay, setLastPlay] = useState(playId);
    if (lastPlay !== playId) {
        setLastPlay(playId);
        setDone(false);
    }
    // Reading and drafting outrank a one-off: while Oura works, nothing else moves.
    const has = useSlot(!reduce && !done, mode === "loop" ? MASCOT_PRIORITY.working : MASCOT_PRIORITY.moment);
    // Every play, a loop's too, starts at its own first frame (the files open on the frame each moment
    // should be seen on first: Thinking's open, reading eyes), never mid-loop on a shared clock.
    const fresh = useFreshSrc(has && !reduce && !done ? MOOD[mood].moving : null, 0);
    const moving = !reduce && !done && has && !!fresh;
    useEffect(() => {
        if (mode !== "once" || !fresh) return;
        const t = window.setTimeout(() => setDone(true), MOOD[mood].ms);
        return () => window.clearTimeout(t);
    }, [mode, fresh, mood]);
    const src = moving ? fresh! : MOOD[mood].still;
    return (
        <span
            aria-hidden
            className={cn(/\b(absolute|fixed)\b/.test(className ?? "") ? "" : "relative", "inline-block shrink-0 select-none", className)}
            style={{ width: size, height: size, ...style }}
            data-ask-mascot={mood}
            data-ask-mascot-still={moving ? undefined : ""}
            data-ask-mascot-played={done ? "" : undefined}
        >
            <Crossfade src={src} size={size} />
        </span>
    );
}

/**
 * The greeting on a new chat: springs in, then the calm Idle loop. Hover gives a wink; while staff type it
 * looks up, attentive. Reduced motion: the Idle still (attentive still while typing), no spring.
 */
export function GreetingMascot({ size, typing }: { size: number; typing: boolean }) {
    useKeyframes();
    const reduce = useReducedMotion();
    const [wink, setWink] = useState(0);
    const [winking, setWinking] = useState(false);
    const wantsLoop = !reduce && !typing;
    const hasLoop = useSlot(wantsLoop, MASCOT_PRIORITY.greeting);
    const hasWink = useSlot(!reduce && winking, MASCOT_PRIORITY.wink);
    const winkSrc = useFreshSrc(winking && hasWink ? idleWink : null, wink);
    // The loop starts at its first frame each time, the calm smile; the squint comes later in the loop.
    const idleSrc = useFreshSrc(hasLoop && wantsLoop ? idle : null, 0);
    useEffect(() => {
        if (!winkSrc) return;
        const t = window.setTimeout(() => setWinking(false), WINK_MS);
        return () => window.clearTimeout(t);
    }, [winkSrc]);
    const state = typing ? "attentive" : winking && winkSrc ? "wink" : hasLoop && wantsLoop && idleSrc ? "idle" : "still";
    const src = state === "attentive" ? attentiveStill : state === "wink" ? winkSrc! : state === "idle" ? idleSrc! : idleStill;
    return (
        <span
            aria-hidden
            className="relative inline-block shrink-0 select-none"
            style={{
                width: size,
                height: size,
                animation: reduce ? undefined : "askMascotSpring 560ms cubic-bezier(0.34, 1.56, 0.64, 1) both",
            }}
            onMouseEnter={() => {
                if (reduce || typing || winking) return;
                setWink((w) => w + 1);
                setWinking(true);
            }}
            data-ask-mascot="idle"
            data-ask-greeting={state}
            data-ask-mascot-still={state === "idle" || state === "wink" ? undefined : ""}
        >
            <span
                className="absolute inset-0 transition-transform duration-300 ease-out motion-reduce:transition-none"
                style={{ transform: typing && !reduce ? "translateY(-2px) scale(1.04)" : undefined }}
            >
                <Crossfade src={src} size={size} />
            </span>
        </span>
    );
}

/** The sizes Oura's still face is drawn at, each with its own 2x file. */
export type FaceSize = 16 | 17 | 20 | 24;
const FACE: Record<FaceSize, string> = { 16: face16, 17: face17, 20: face20, 24: face24 };
/** A moment grows out of the face a little, so the expression reads at chat sizes. */
const POP = 1.35;

/**
 * Oura's still face, where Oura acts as the assistant. `react` plays one moment (Love, Surprised) once, grown
 * out of the face in place, then settles back to the face. `wink`: hovering it gives a quick wink.
 */
export function OuraFace({
    size,
    className,
    react,
    wink,
    delay = 0,
    playKey,
}: {
    size: FaceSize;
    className?: string;
    react?: "love" | "surprised" | null;
    wink?: boolean;
    /** ms before the moment starts, so it follows what is already moving (the greeting's spring). */
    delay?: number;
    /** A new value plays the reaction again, even the same one. */
    playKey?: string | number;
}) {
    useKeyframes();
    const reduce = useReducedMotion();
    const [played, setPlayed] = useState(false);
    const [due, setDue] = useState(delay <= 0);
    const [settling, setSettling] = useState(false);
    // A new reaction (or a new play of the same one) starts over: not played, waiting out its delay.
    const playId = `${react ?? ""}|${playKey ?? ""}`;
    const [lastPlay, setLastPlay] = useState(playId);
    if (lastPlay !== playId) {
        setLastPlay(playId);
        setPlayed(false);
        setSettling(false);
        setDue(delay <= 0);
    }
    useEffect(() => {
        if (due || !react) return;
        const t = window.setTimeout(() => setDue(true), delay);
        return () => window.clearTimeout(t);
    }, [due, react, delay]);
    const reacting = !!react && !played && due;
    const hasReact = useSlot(!reduce && reacting, MASCOT_PRIORITY.moment);
    const reactSrc = useFreshSrc(react && reacting && hasReact && !reduce ? MOOD[react].moving : null, 0);
    useEffect(() => {
        if (!reactSrc || !react) return;
        const t = window.setTimeout(() => {
            setPlayed(true);
            setSettling(true);
        }, MOOD[react].ms);
        return () => window.clearTimeout(t);
    }, [reactSrc, react]);
    useEffect(() => {
        if (!settling) return;
        const t = window.setTimeout(() => setSettling(false), 300);
        return () => window.clearTimeout(t);
    }, [settling]);

    const [winkPlay, setWinkPlay] = useState(0);
    const [winking, setWinking] = useState(false);
    const hasWink = useSlot(!reduce && winking, MASCOT_PRIORITY.wink);
    const winkSrc = useFreshSrc(winking && hasWink ? faceWink : null, winkPlay);
    useEffect(() => {
        if (!winkSrc) return;
        const t = window.setTimeout(() => setWinking(false), WINK_MS);
        return () => window.clearTimeout(t);
    }, [winkSrc]);

    // The moment's canvas, sized so its helmet is the face's helmet grown by POP and centred on it.
    // Reduced motion: the mood's still, at the face's own size, held.
    const showReact = !!react && (reduce || (reacting && !!reactSrc));
    const grow = reduce ? 1 : POP;
    const box = (size * FACE_HELMET * grow) / REST.w;
    const left = size / 2 - REST.cx * box;
    const top = size / 2 - REST.cy * box;
    const faceSrc = winking && winkSrc ? winkSrc : FACE[size];
    const face = (
        <img
            src={faceSrc}
            alt=""
            aria-hidden
            width={size}
            height={size}
            draggable={false}
            className="block select-none"
            style={{ width: size, height: size, opacity: showReact ? 0 : 1, transition: reduce ? undefined : "opacity 200ms ease-out" }}
            data-ask-face={size}
        />
    );
    if (!react && !wink) {
        return (
            <img
                src={FACE[size]}
                alt=""
                aria-hidden
                width={size}
                height={size}
                draggable={false}
                className={cn("shrink-0 select-none", className)}
                style={{ width: size, height: size }}
                data-ask-face={size}
            />
        );
    }
    return (
        <span
            aria-hidden
            className={cn(/\b(absolute|fixed)\b/.test(className ?? "") ? "" : "relative", "inline-block shrink-0 select-none", className)}
            style={{ width: size, height: size }}
            onMouseEnter={
                wink
                    ? () => {
                          if (reduce || winking || reacting) return;
                          setWinkPlay((w) => w + 1);
                          setWinking(true);
                      }
                    : undefined
            }
            data-ask-face-wrap
            data-ask-face-winking={winking && winkSrc ? "" : undefined}
            data-ask-face-reacted={react && (played || reduce) ? react : undefined}
        >
            {face}
            {winking && winkSrc && <span className="pointer-events-none absolute inset-0" data-ask-mascot="wink" />}
            {settling && !reduce && (
                // The moment ends on Oura's calm smile: shrink back into the face while it fades in.
                <span
                    className="pointer-events-none absolute"
                    style={{ left, top, width: box, height: box, transformOrigin: `${REST.cx * 100}% ${REST.cy * 100}%`, animation: "askMascotSettle 280ms ease-out both" }}
                >
                    <img src={idleStill} alt="" aria-hidden draggable={false} className="block select-none" style={{ width: box, height: box }} />
                </span>
            )}
            {showReact && react && (
                <span
                    className="pointer-events-none absolute"
                    style={{ left, top, width: box, height: box, transformOrigin: `${REST.cx * 100}% ${REST.cy * 100}%`, animation: reduce ? undefined : "askMascotPop 420ms cubic-bezier(0.34, 1.56, 0.64, 1) both" }}
                    data-ask-mascot={react}
                    data-ask-mascot-still={reduce ? "" : undefined}
                >
                    <img src={reduce ? MOOD[react].still : reactSrc!} alt="" aria-hidden draggable={false} className="block select-none" style={{ width: box, height: box }} />
                </span>
            )}
        </span>
    );
}
